import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import * as React from "react";

import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { useFetchNearbyPrices } from "@/hooks/useManagementDetailData";
import theme from "@/theme";

import { Loading } from "../Loading";

const useStyles = makeStyles({
  root: {
    padding: layout.space[24],
    background: colors["surface/card"],
    [theme.breakpoints.up("md")]: { padding: 0, background: "transparent" },
  },
  description: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    marginBottom: layout.space[16],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"], marginBottom: layout.space[24] },
  },
  cards: { display: "grid", gap: layout.space[12] },
  card: { padding: layout.space[16], borderRadius: layout.radius.md, background: colors["surface/base"] },
  price: { ...textStyles["Number/S"] },
  small: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"] },
  table: { background: colors["surface/card"], borderRadius: layout.radius.lg, overflowX: "auto" },
  cell: {
    ...textStyles["PC/Body/Small"],
    borderColor: colors["border/default"],
    padding: "16px 12px",
    whiteSpace: "nowrap",
    "&:first-child": { paddingLeft: layout.space[24] },
    "&:last-child": { paddingRight: layout.space[24] },
  },
  head: { "& th": { color: colors["text/secondary"], paddingTop: layout.space[12], paddingBottom: layout.space[12] } },
  source: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    textAlign: "right",
    marginTop: layout.space[16],
  },
});

export const ManagementDetailNearBy = ({ propertyId }: { propertyId: number }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const { nearbyPrices, loading: fetching } = useFetchNearbyPrices(propertyId);
  if (fetching) return <Loading />;
  return (
    <Box className={classes.root}>
      <Typography className={classes.description}>
        この物件の周辺で実際に売買された価格です。売却を考えるときの目安になります。
      </Typography>
      {!nearbyPrices?.length ? (
        <Typography className={classes.description}>
          同エリアで情報が見つかりませんでした。対象物件の市区町村が未登録の場合は登録してください。
        </Typography>
      ) : isMobile ? (
        <Box className={classes.cards}>
          {nearbyPrices.map((price, index) => (
            <Box key={index} className={classes.card}>
              <Box display="flex" alignItems="baseline" justifyContent="space-between" gridGap={8}>
                <Typography className={classes.price}>
                  {Math.round(Number(price.TradePrice) / 10000).toLocaleString()}
                  <Box component="span" className={classes.small}>
                    {" "}
                    万円
                  </Box>
                </Typography>
                <Typography className={classes.small}>{price.Period}</Typography>
              </Box>
              <Typography className={classes.small} style={{ marginTop: layout.space[8] }}>
                {price.Municipality + price.DistrictName} ・ {price.Area}㎡ ・ {price.BuildingYear} ・ {price.Structure}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer className={classes.table}>
          <Table>
            <TableHead className={classes.head}>
              <TableRow>
                {["成約価格", "取引時期", "所在地", "面積", "築年", "構造"].map((label) => (
                  <TableCell key={label} className={classes.cell}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {nearbyPrices.map((price, index) => (
                <TableRow key={index}>
                  <TableCell className={classes.cell}>
                    <span className={classes.price}>
                      {Math.round(Number(price.TradePrice) / 10000).toLocaleString()}
                    </span>{" "}
                    万円
                  </TableCell>
                  <TableCell className={classes.cell}>{price.Period}</TableCell>
                  <TableCell className={classes.cell}>{price.Municipality + price.DistrictName}</TableCell>
                  <TableCell className={classes.cell}>{price.Area}㎡</TableCell>
                  <TableCell className={classes.cell}>{price.BuildingYear}</TableCell>
                  <TableCell className={classes.cell}>{price.Structure}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography className={classes.source}>出典：国土交通省 不動産情報ライブラリ</Typography>
    </Box>
  );
};
